import { ThunkDispatch } from 'redux-thunk';
import apis, { fetchWithSSE } from '../../utils/apis';
import { ONBOARD_CLIENT, ONBOARD_CLIENT_COMPLETED, ONBOARD_CLIENT_CONNECTED, ONBOARD_CLIENT_CONNECTING, ONBOARD_CLIENT_DONE, ONBOARD_CLIENT_FAILED, ONBOARD_CLIENT_STEP, ONBOARD_CLIENT_STEPS } from '../types';
import { RootState } from '..';
import { AnyAction } from 'redux';
import { config } from '../../utils/config';
import { showFeedbackModal } from './UserFeedbackActions';
import { handleApiError } from './utils/utils';
import { ThunkAction } from '@reduxjs/toolkit';
import { translate } from '../../i18n/translate';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

// Types
export interface Client {
  id: number;
  uuid: string;
  name: string;
  type_id: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  is_onboarded: boolean;
  is_individual?: boolean;
  contact_person_name?: string;
  contact_person_email?: string;
  organization_id?: number;
  user_id?: number;
  created_at: string;
  updated_at: string;
  client_metadata?: {
    MatterCount?: number;
    assignedAttorneys?: Array<{ FullName?: string }>;
    contact_person_name?: string;
    contact_person_email?: string;
    [key: string]: any;
  };
}

export interface ClientProfile {
  name: string;
  type_id: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  company_name?: string;
  industry?: string;
  years_in_business?: number;
  employee_count?: number;
  website?: string;
  social_media?: string[];
  timezone?: string;
  language?: string;
  created_at: string;
  updated_at: string;
}

export interface ClientsListResponse {
  success: boolean;
  clients: Client[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface ClientDetailsResponse {
  success: boolean;
  client: Client;
  profile: ClientProfile;
}

export interface ClientCreateRequest {
  name: string;
  type_id: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  client_metadata?: Record<string, any>;
}

export interface ClientUpdateRequest {
  name?: string;
  type_id?: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active?: boolean;
  contact_person_name?: string;
  contact_person_email?: string;
  client_metadata?: Record<string, any>;
  profile?: Partial<ClientProfile>;
}

export const clientActions = {
  // Create a new client
  // async createClient(clientData: ClientCreateRequest) {
  //   try {
  //     const response = await apis({
  //       method: 'POST',
  //       url: '/clients',
  //       data: clientData,
  //     });

  //     if (!response.data.success) {
  //       throw new Error(response.data.error || 'Failed to create client');
  //     }

  //     return response.data;
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.error || error.message || 'Failed to create client';
  //     throw new Error(errorMessage);
  //   }
  // },

  createClientSimple(data: {
    name: string;
    type_id?: string | number;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    notes?: string;
    contact_person_name?: string;
    contact_person_email?: string;
    client_metadata?: { [key: string]: any };
  }) {
    return async (dispatch: any): Promise<any> => {
      try {
        const payload = {
          name: `${data.name || ''}`.trim(),
          type_id: data.type_id ? parseInt(String(data.type_id)) : undefined,
          contact_email: data.contact_email || undefined,
          contact_phone: data.contact_phone || undefined,
          address: data.address || undefined,
          notes: data.notes || undefined,
          contact_person_name: data.contact_person_name || undefined,
          contact_person_email: data.contact_person_email || undefined,
          client_metadata: data.client_metadata || {},
        };
  
        const response = await apis({
          method: "POST",
          url: "/clients",
          data: payload,
        });
  
        if (response && response.status === 201 && response.data?.success) {
          dispatch({ type: ONBOARD_CLIENT, payload: response.data.data });
          return response.data.data;
        } else {
          const errorMsg = toErrorText(response?.data?.error) || "Failed to create client";
          dispatch({ type: ONBOARD_CLIENT_FAILED, payload: { error: errorMsg } });
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        const errorMsg = error?.message || "Failed to create client";
        dispatch({ type: ONBOARD_CLIENT_FAILED, payload: { error: errorMsg } });
        throw error;
      }
    };
  },

  // List clients with pagination, sorting, and filtering
  async listClients(page = 1, pageSize = 12, options?: { sort_by?: string; sort_order?: 'asc' | 'desc'; filters?: Record<string, string[]>; is_active?: boolean }) {
    try {
      const params: any = {
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      if (options?.sort_by) {
        params.sort_by = options.sort_by;
      }
      if (options?.sort_order) {
        params.sort_order = options.sort_order;
      }
      if (typeof options?.is_active === 'boolean') {
        params.is_active = String(options.is_active);
      }

      // Add filter parameters for each selected filter
      if (options?.filters) {
        Object.entries(options.filters).forEach(([filterKey, filterValues]) => {
          if (filterValues && filterValues.length > 0) {
            // Map 'type' filter section to 'type_id' query parameter
            const paramKey = filterKey === 'type' ? 'type_id' : filterKey;
            params[paramKey] = filterValues.join(',');
          }
        });
      }

      const response = await apis({
        method: 'GET',
        url: '/clients',
        params,
      });

      // Check if the API returned an error response
      if (response.data.success === false) {
        const errorMsg = toErrorText(response.data.error) || 'Failed to fetch clients';
        throw new Error(errorMsg);
      }

      // Handle new API response structure where clients are in data.data
      const clientsArray = response.data.data || response.data.clients || [];

      // Ensure we have a valid clients array
      if (!Array.isArray(clientsArray)) {
        console.warn('Clients response missing or invalid clients array, returning empty list');
        return {
          success: true,
          clients: [],
          pagination: {
            page: page,
            page_size: pageSize,
            total: 0,
            total_pages: 0,
          },
        };
      }

      // Handle empty client list response (successful API call with no clients)
      return {
        success: response.data.success ?? true,
        clients: clientsArray,
        pagination: response.data.pagination || {
          page: response.data.page || page,
          page_size: response.data.page_size || pageSize,
          total: response.data.total || 0,
          total_pages: response.data.total_pages || 0,
        },
      } as ClientsListResponse;
    } catch (error: any) {
      // Don't mask API errors - let them propagate so components can handle them
      const errorMessage = readErrorMessage(error, 'Failed to fetch clients');
      throw new Error(errorMessage);
    }
  },

  // Get client details with profile
  async getClientDetails(clientUuid: string) {
    try {
      const response = await apis({
        method: 'GET',
        url: `/clients/${clientUuid}`,
      });

      if (!response.data.success) {
        throw new Error(toErrorText(response.data.error) || 'Failed to fetch client');
      }

      return response.data as ClientDetailsResponse;
    } catch (error: any) {
      const errorMessage = readErrorMessage(error, 'Failed to fetch client');
      throw new Error(errorMessage);
    }
  },

  // Update client
  async updateClient(clientUuid: string, clientData: ClientUpdateRequest) {
    try {
      const response = await apis({
        method: 'PUT',
        url: `/clients/${clientUuid}`,
        data: clientData,
      });

      if (!response.data.success) {
        throw new Error(toErrorText(response.data.error) || 'Failed to update client');
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = readErrorMessage(error, 'Failed to update client');
      throw new Error(errorMessage);
    }
  },

  // Delete client (soft delete)
  async deleteClient(clientUuid: string) {
    try {
      const response = await apis({
        method: 'DELETE',
        url: `/clients/${clientUuid}`,
      });

      if (!response.data.success) {
        throw new Error(toErrorText(response.data.error) || 'Failed to delete client');
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = readErrorMessage(error, 'Failed to delete client');
      throw new Error(errorMessage);
    }
  },

};

// Export individual action functions for convenience
// export const createClient = clientActions.createClient;
export const createClientSimple = clientActions.createClientSimple;
export const listClients = clientActions.listClients;
export const getClientDetails = clientActions.getClientDetails;
export const updateClient = clientActions.updateClient;
export const deleteClient = clientActions.deleteClient;

/**
 * Bulk delete multiple clients in a single API call.
 * Returns { success, deleted_count, failed_count, total_count, failed_items }.
 */
export const bulkDeleteClients = async (clientUuids: string[]): Promise<{
  success: boolean;
  deleted_count: number;
  failed_count: number;
  total_count: number;
  failed_items: { uuid: string; error: string }[];
}> => {
  try {
    const response = await apis.post('/clients/bulk-delete', {
      client_uuids: clientUuids,
    });
    return response.data;
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to delete clients');
    throw new Error(errorMessage);
  }
};

/**
 * Download a client document
 * Triggers a browser download of the document
 */
export const downloadClientDocument = (
  clientUuid: string,
  documentUuid: string,
  filename: string
): ((dispatch: any) => Promise<void>) =>
  async (dispatch) => {

    try {
      const response = await apis({
        method: "GET",
        url: `/clients/${clientUuid}/documents/${documentUuid}`,
        responseType: 'blob'
      });

      if (response.status !== 200) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = response.data;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Invalid file received');
      }

      // Create object URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      throw error;
    }
  };

/**
 * View/stream a client document
 * Fetches the document blob from the server with proper streaming and decryption
 */
export const viewClientDocument = (
  clientUuid: string,
  documentUuid: string,
  filename: string
): ((dispatch: any) => Promise<{ success: boolean; blob?: Blob; contentType?: string; filename?: string; error?: string }>) =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/clients/${clientUuid}/documents/${documentUuid}/view-content`,
        responseType: 'blob'
      });


      if (!response) {
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.status !== 200) {
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      // Validate blob exists and is valid
      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      // Check blob size - empty blobs indicate an error
      if (blob.size === 0) {
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      // Check if server returned an HTML error page instead of the file
      if (blob.type === 'text/html' || blob.type === '') {
        return {
          success: false,
          error: 'Server returned an error. File may not exist or access was denied.'
        };
      }

      // Get the actual content-type from response headers
      const contentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || 'application/octet-stream';

      // Get filename from Content-Disposition header or use provided
      let viewFilename = filename;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          viewFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }


      return {
        success: true,
        blob,
        contentType,
        filename: viewFilename
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Error viewing document'
      };
    }
  };




export const ImportClientSSE =
  (data: Record<string, any>, files?: File[] | { [key: string]: File }) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    return new Promise<any>(async (resolve, reject) => {
      dispatch({ type: ONBOARD_CLIENT_CONNECTING });

      try {
        const fd = new FormData();

        const appendKV = (key: string, val: any) => {
          if (val === undefined || val === null) return;
          if (val instanceof File) {
            fd.append(key, val, val.name);
          } else if (val instanceof Date) {
            fd.append(key, val.toISOString());
          } else if (typeof val === "object") {
            fd.append(key, JSON.stringify(val));
          } else {
            fd.append(key, String(val));
          }
        };

        Object.entries<any>(data || {}).forEach(([k, v]) => appendKV(k, v));

        if (Array.isArray(files)) {
          files.forEach((f) => f && fd.append("files[]", f, f.name));
        } else if (files && typeof files === "object") {
          Object.values(files).forEach((f) => f && fd.append("files[]", f, f.name));
        }

        const resp = await fetchWithSSE(`${config.baseUrl}/clients/import_clients`, {
          method: "POST",
          body: fd,
        });

        if (!resp.ok) {
          const msg = `HTTP ${resp.status}`;
          dispatch({ type: ONBOARD_CLIENT_FAILED, payload: { error: msg } });
          return reject(new Error(msg));
        }
        if (!resp.body) {
          const msg = "No SSE body returned";
          dispatch({ type: ONBOARD_CLIENT_FAILED, payload: { error: msg } });
          return reject(new Error(msg));
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });

          chunk.split("\n\n").forEach((raw) => {
            if (!raw || !raw.trim()) return;

            let event = "message";
            let dataStr = "";

            raw.split("\n").forEach((line) => {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              if (line.startsWith("data:"))  dataStr += line.slice(5).trim();
            });

            if (event === "done") {
              dispatch({ type: ONBOARD_CLIENT_DONE });
              return;
            }

            try {
              const parsed = dataStr ? JSON.parse(dataStr) : null;

              switch (event) {
                case "connected":
                  dispatch({ type: ONBOARD_CLIENT_CONNECTED, payload: parsed });
                  break;

                case "steps":
                  dispatch({ type: ONBOARD_CLIENT_STEPS, payload: parsed });
                  break;

                case "step":
                  dispatch({ type: ONBOARD_CLIENT_STEP, payload: parsed });
                  break;

                case "completed": {
                  dispatch({ type: ONBOARD_CLIENT_COMPLETED, payload: parsed });

                  if (parsed?.ok) {
                    dispatch(
                      showFeedbackModal({
                        showModal: true,
                        message: parsed?.message || "Client onboarded successfully.",
                        modalType: "success",
                        duration: 2500,
                      })
                    );
                    resolve(parsed);
                  } else {
                    dispatch(
                      showFeedbackModal({
                        showModal: true,
                        message: parsed?.message || "Onboarding failed.",
                        modalType: "error",
                        duration: 3500,
                      })
                    );
                    reject(parsed || { ok: false });
                  }
                  break;
                }

                case "error":
                  dispatch({ type: ONBOARD_CLIENT_FAILED, payload: parsed });
                  reject(parsed);
                  break;

                default:
                  break;
              }
            } catch {
            }
          });
        }
      } catch (err: any) {
        const msg = String(err?.message || err);
        dispatch({ type: ONBOARD_CLIENT_FAILED, payload: { error: msg } });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate("Onboarding failed."),
            modalType: "error",
            duration: 3500,
          })
        );
        reject(err);
      }
    });
  };


export const attachExistingCaseToClient = (clientId: string, matterId: string) => {
    return async (dispatch: any) => {
      try {
        const resp = await apis.post('/clients/attach_existing_case', {
          ClientID: clientId,
          MatterID: matterId,
        });
        if (resp && resp.status === 200) {
          dispatch(showFeedbackModal({
            modalType: 'success',
            message:  resp.data.message || 'Matter attached to client successfully',
            showModal: true,
            duration: 3000
          }));
          // dispatch(fetchAllClientDetails(clientId));
          return true;
        } else {
          handleApiError(resp, dispatch);
          return false;
        }
      } catch (err) {
        handleApiError(err, dispatch);
        return false;
      }
    };
  };


export const importClients = (formData: FormData) => {
    return async (dispatch: any): Promise<void> => {
      try {
        const uploadResp = await apis({
          method: 'POST',
          url: 'clients/upload_files',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
  
        if (uploadResp.status === 202 && uploadResp.data.job_id) {
          const jobId = uploadResp.data.job_id;
          localStorage.setItem('importJobId', jobId);
          dispatch({ type: 'START_IMPORT_PROGRESS', payload: { jobId } });
        } else {
          throw new Error('Upload failed or no job_id returned');
        }
      } catch (err: any) {
        dispatch(
          showFeedbackModal({
            modalType: 'error',
            message: translate('Failed to import clients. Please try again.'),
            showModal: true,
            duration: 3000,
          })
        );
        throw err;
      }
    };
  };

export const handleSendOnboardingEmails = (
    clientIds: string[],
    customMessage: string
  ): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async (dispatch) => {
      try {
        await apis({
          method: 'POST',
          url: '/clients/send_onboarding_emails',
          data: { client_ids: clientIds, custom_message: customMessage },
        });
        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: translate('Onboarding emails sent successfully.'),
            showModal: true,
            duration: 3000,
          })
        );
        dispatch({ type: 'CLEAR_SELECTED_CLIENTS' }); 
      } catch (error: any) {
        if(!error) return
        dispatch(
          showFeedbackModal({
            modalType: 'error',
            message: translate('Failed to send onboarding emails.'),
            showModal: true,
            duration: 3000,
          })
        );
      }
    };
  };
